// When each page in the sitemap was first published and when its content last
// changed, keyed by path. The single source for the visible "Updated" line,
// the JSON-LD datePublished/dateModified and the sitemap <lastmod>
// (vite.config.ts fails the build if a sitemap route has no entry here).
//
// Every date is a git author date (`git log --format=%as`), taken per entry
// rather than per file, with the commits noted beside it (first, last):
// - published: the first commit in which the page had content, i.e. its slug
//   first appeared in seo-content.ts or destinations.ts, or its page
//   component was added.
// - modified: the last commit that changed what the page shows: the
//   article's entry in seo-content.ts, or glossary-articles.ts for the five
//   rewritten explainers; the destination's entry in destinations.ts and its
//   guide in destination-guides.ts; or the page's own copy. Layout,
//   breadcrumb, schema-only and comment-only commits don't count.
// When you change a page's content, set its `modified` to that day.

export interface PageDates {
  published: string; // YYYY-MM-DD
  modified: string; // YYYY-MM-DD
}

export const PAGE_DATES: Record<string, PageDates> = {
  // Product and hub pages (sitemap <lastmod> only; no visible date).
  '/': { published: '2026-05-14', modified: '2026-10-05' }, // f9be698, 140cbf6 + cc78b65
  '/explore': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/travel-intelligence': { published: '2026-05-15', modified: '2026-09-17' }, // 0b65d1a, 32c5ec1
  '/corporate-travel': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/knowledge': { published: '2026-05-20', modified: '2026-09-28' }, // 8800172, 1288e33
  '/privacy': { published: '2026-05-22', modified: '2026-05-22' }, // c395804 ("Last Updated: May 22, 2026")
  '/about': { published: '2026-09-28', modified: '2026-10-05' }, // 1288e33, 6813aa9

  // Destination guides.
  '/destinations/new-york-city': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/destinations/jfk-airport': { published: '2026-05-15', modified: '2026-10-05' }, // df3da5a, cc78b65
  '/destinations/london': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/destinations/london-heathrow': { published: '2026-05-15', modified: '2026-10-05' }, // df3da5a, cc78b65
  '/destinations/tokyo': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/destinations/dubai': { published: '2026-05-15', modified: '2026-10-05' }, // 0b65d1a, cc78b65
  '/destinations/business-travel-orchestration': { published: '2026-05-15', modified: '2026-10-05' }, // df3da5a, cc78b65

  // Articles.
  '/knowledge/flight-delay-prediction-ai': { published: '2026-05-20', modified: '2026-10-05' }, // 8800172, a7d7a9b
  '/knowledge/smart-itinerary-optimization': { published: '2026-05-20', modified: '2026-10-05' }, // 8800172, a7d7a9b
  '/knowledge/autonomous-multi-agent-travel': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/death-of-static-itinerary': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/generative-engine-optimization-travel': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/business-travel-orchestration-2026': { published: '2026-05-20', modified: '2026-10-05' }, // 8800172, a7d7a9b
  '/knowledge/enterprise-travel-saas-vs-orchestration': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/decarbonizing-travel-route-optimization': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, 36aae11
  '/knowledge/voyara-ai-eclipse-group-ecosystem': { published: '2026-05-20', modified: '2026-10-05' }, // 43a23c7, a7d7a9b
  '/knowledge/eclipse-group-vs-eclipse-foundation': { published: '2026-05-20', modified: '2026-10-05' }, // 43a23c7, a7d7a9b
  '/knowledge/vyora-vayora-voyara-ai': { published: '2026-05-20', modified: '2026-10-05' }, // 43a23c7, a7d7a9b

  // Glossary explainers: live as templated entries from bbabb19, rewritten in 3de869d;
  // sources, comparison tables and question headings added in 85dacbc..a7d7a9b.
  '/knowledge/computer-reservation-system-crs': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/global-distribution-system-gds': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/passenger-name-record-pnr': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/online-booking-tool-obt': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
  '/knowledge/virtual-credit-cards': { published: '2026-05-22', modified: '2026-10-05' }, // bbabb19, a7d7a9b
};

export const getPageDates = (path: string): PageDates | undefined => PAGE_DATES[path];

// The dates are calendar days, so they are read and shown in UTC; in a local
// time zone west of UTC, "2026-10-05" would display as 4 October.
export const toUtcDate = (isoDate: string): Date => new Date(`${isoDate}T00:00:00Z`);

const DISPLAY_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

// "5 October 2026"
export const formatDisplayDate = (isoDate: string): string => DISPLAY_FORMAT.format(toUtcDate(isoDate));
